import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

import ApiClient from '../../config/ApiClient';
import {
    AssignmentInvitation,
    AssignmentInvitationRole,
} from '../../object-actions/types/types';

interface PendingInvitationsListProps {
    assignmentId: number;
    invitations: AssignmentInvitation[];
    role?: AssignmentInvitationRole;
    onChanged?: () => void;
}

const PendingInvitationsList: React.FC<PendingInvitationsListProps> = ({
    assignmentId,
    invitations,
    role,
    onChanged,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [busyId, setBusyId] = useState<number | null>(null);

    const filtered = (invitations || []).filter(
        (inv) => inv.action === 'pending' && (!role || inv.role === role),
    );

    if (filtered.length === 0) return null;

    const handleResend = async (invitation: AssignmentInvitation) => {
        setBusyId(invitation.id);
        try {
            const res = await ApiClient.post(
                `/api/payment-assignments/${assignmentId}/invitations/${invitation.id}/resend/`,
                {},
            );
            if (res.success) {
                enqueueSnackbar('Invitation re-sent', { variant: 'success' });
                if (onChanged) onChanged();
            } else {
                const retryAfter =
                    getRetryAfterSeconds(res.errors) ?? getRetryAfterSeconds(res.error);
                if (retryAfter !== null) {
                    const minutes = Math.max(1, Math.ceil(retryAfter / 60));
                    enqueueSnackbar(
                        `Sent recently — try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`,
                        { variant: 'warning' },
                    );
                } else {
                    enqueueSnackbar(formatError(res.error), { variant: 'error' });
                }
            }
        } catch (e: any) {
            enqueueSnackbar(e?.message || 'Failed to resend invitation', { variant: 'error' });
        } finally {
            setBusyId(null);
        }
    };

    const handleRevoke = async (invitation: AssignmentInvitation) => {
        setBusyId(invitation.id);
        try {
            const res = await ApiClient.delete(
                `/api/payment-assignments/${assignmentId}/invitations/${invitation.id}/`,
            );
            if (res.success) {
                enqueueSnackbar('Invitation revoked', { variant: 'success' });
                if (onChanged) onChanged();
            } else {
                enqueueSnackbar(formatError(res.error), { variant: 'error' });
            }
        } catch (e: any) {
            enqueueSnackbar(e?.message || 'Failed to revoke invitation', { variant: 'error' });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Box sx={{ mt: 1 }} data-attr={`pending-invitations-list_role-${role || 'all'}`}>
            <Typography variant="caption" color="text.secondary">
                Pending invitations
            </Typography>
            <List dense disablePadding>
                {filtered.map((inv) => {
                    const isBusy = busyId === inv.id;
                    const destination = inv.masked_destination || inv.invitee_email || inv.invitee_phone || '—';
                    const primaryLabel = inv.invitee_name
                        ? `${inv.invitee_name} (${destination})`
                        : destination;
                    return (
                        <ListItem key={inv.id} disableGutters sx={{ pr: 12 }}>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {inv.channel === 'sms' ? (
                                            <SmsIcon fontSize="small" color="action" />
                                        ) : (
                                            <EmailIcon fontSize="small" color="action" />
                                        )}
                                        <Typography variant="body2">{primaryLabel}</Typography>
                                        <Chip
                                            size="small"
                                            label={`Pending • ${inv.channel.toUpperCase()}`}
                                            color="default"
                                            variant="outlined"
                                        />
                                    </Box>
                                }
                                secondary={
                                    inv.expires
                                        ? `Expires ${new Date(inv.expires).toLocaleDateString()}`
                                        : undefined
                                }
                            />
                            <ListItemSecondaryAction>
                                <Tooltip title="Resend invitation">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleResend(inv)}
                                            disabled={isBusy}
                                            data-attr={`pending-invitations-list_resend-${inv.id}`}
                                        >
                                            {isBusy ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Revoke invitation">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRevoke(inv)}
                                            disabled={isBusy}
                                            data-attr={`pending-invitations-list_revoke-${inv.id}`}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};

function getRetryAfterSeconds(payload: unknown): number | null {
    if (payload == null || typeof payload !== 'object') {
        return null;
    }
    const seconds = Number((payload as { retry_after_seconds?: unknown }).retry_after_seconds);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function formatError(err: any): string {
    if (!err) return 'Request failed';
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
        const flat = Object.values(err).flat();
        if (flat.length) return flat.join('; ');
    }
    return 'Request failed';
}

export default PendingInvitationsList;
